import { z } from 'zod'
import { BaseFormAnswerSchema } from '@omnea-app/packages/universal/formBuilder/BaseFormQuestionAndAnswerSchemas'
import { ref } from '@omnea-app/packages/universal/references/refKinds'
import { sensitive } from '@omnea-app/packages/universal/sensitive-data/sensitive'

// The entity types that can be used for custom fields in request forms
export const RequestFormCustomFieldEntityTypeSchema = z.enum([
  'supplier',
  'purchase-order',
  'supplier-profile',
  'bank-account',
  'user-attribute',
  'agreement-term',
  'category',
  'market',
])
export type RequestFormCustomFieldEntityType = z.infer<typeof RequestFormCustomFieldEntityTypeSchema>

// Every entity type a form can reference a custom field of: a question's `customField` takes the request-form
// ones, and a lineItems question references `line-item` fields by keying `fieldConfigsById` with their id.
export const FORM_REFERENCED_CUSTOM_FIELD_ENTITY_TYPES = [...RequestFormCustomFieldEntityTypeSchema.options, 'line-item'] as const

export const RequestFormCustomFieldPropertySchema = z.object({
  // The binding itself: a question carrying this depends on the field, and the entity type beside it
  // narrows nothing, since a custom field id is unique across them.
  id: ref('customField'),
  type: RequestFormCustomFieldEntityTypeSchema,
})
export type RequestFormCustomFieldProperty = z.infer<typeof RequestFormCustomFieldPropertySchema>

export const isBoundToCustomFieldOf = (answer: object, entityType: RequestFormCustomFieldEntityType): boolean =>
  'customField' in answer &&
  typeof answer.customField === 'object' &&
  answer.customField !== null &&
  'type' in answer.customField &&
  answer.customField.type === entityType

const isBankAccountAnswer = (answer: object): boolean => isBoundToCustomFieldOf(answer, 'bank-account')

// The answer to a bank account's custom field is bank data, whatever its answer type. Its metadata can quote it.
export const customFieldAnswerProperties = <T extends z.ZodType>(value: T) => ({
  value: sensitive('bank', value, isBankAccountAnswer),
  metadata: sensitive('bank', BaseFormAnswerSchema.shape.metadata, isBankAccountAnswer),
  customField: RequestFormCustomFieldPropertySchema.optional(),
})
